import { useState, useEffect, useCallback, useRef } from 'react'
import { create } from '@bufbuild/protobuf'
import { toCanvas } from 'html-to-image'
import { CreateSupportThreadRequestSchema } from '@/gen/cortado/v1/support_pb'
import { useCurrentOrganization } from '@/stores/organization.store'
import { useSupportCreateThread } from '@/stores/support.store'
import { createSupportClientContext, currentPageUrl } from '@/lib/support-context'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import { ArrowLeft, Send, Camera, X } from 'lucide-react'

const MAX_SCREENSHOTS = 3
const MAX_WIDTH = 1920

interface Screenshot {
  blob: Blob
  url: string
  /**
   * The page this capture was taken on. Recorded per screenshot rather than
   * once at submit time: the panel stays open while the user navigates, so two
   * captures in one thread can come from two different pages.
   */
  pageUrl: string
}

interface NewThreadComposerProps {
  onBack: () => void
  onThreadCreated: (threadId: string) => void
}

export function NewThreadComposer({ onBack, onThreadCreated }: NewThreadComposerProps) {
  const [body, setBody] = useState('')
  const [includeScreenshots, setIncludeScreenshots] = useState(false)
  const [screenshots, setScreenshots] = useState<Screenshot[]>([])
  const [capturing, setCapturing] = useState(false)
  const [captureError, setCaptureError] = useState(false)
  const { currentOrganization } = useCurrentOrganization()
  const orgId = currentOrganization?.id || ''
  const screenshotsRef = useRef<Screenshot[]>([])

  const { mutation } = useSupportCreateThread(onThreadCreated)

  useEffect(() => {
    screenshotsRef.current = screenshots
  }, [screenshots])

  useEffect(() => {
    return () => {
      screenshotsRef.current.forEach((s) => URL.revokeObjectURL(s.url))
    }
  }, [])

  const captureScreenshot = useCallback(async () => {
    if (screenshots.length >= MAX_SCREENSHOTS || capturing) return
    setCapturing(true)
    setCaptureError(false)
    // Read the URL before the await: capture is async, and the user can
    // navigate while it runs.
    const pageUrl = currentPageUrl()
    try {
      const canvas = await toCanvas(document.body, {
        filter: (node) =>
          !(node instanceof HTMLElement && node.hasAttribute('data-html2canvas-ignore')),
      })
      let finalCanvas = canvas

      if (canvas.width > MAX_WIDTH) {
        const scale = MAX_WIDTH / canvas.width
        const resized = document.createElement('canvas')
        resized.width = MAX_WIDTH
        resized.height = Math.round(canvas.height * scale)
        const ctx = resized.getContext('2d')
        if (ctx) {
          ctx.drawImage(canvas, 0, 0, resized.width, resized.height)
          finalCanvas = resized
        }
      }

      const blob = await new Promise<Blob | null>((resolve) => {
        finalCanvas.toBlob(resolve, 'image/jpeg', 0.7)
      })

      if (blob) {
        const url = URL.createObjectURL(blob)
        setScreenshots((prev) => [...prev, { blob, url, pageUrl }])
      }
    } catch {
      setCaptureError(true)
    } finally {
      setCapturing(false)
    }
  }, [screenshots.length, capturing])

  function removeScreenshot(index: number) {
    setScreenshots((prev) => {
      const removed = prev[index]
      if (removed) URL.revokeObjectURL(removed.url)
      return prev.filter((_, i) => i !== index)
    })
  }

  async function handleSubmit() {
    const trimmed = body.trim()
    if (!trimmed || mutation.isPending) return

    let screenshotCaptures: { image: Uint8Array; pageUrl: string }[] = []
    if (includeScreenshots && screenshots.length > 0) {
      screenshotCaptures = await Promise.all(
        screenshots.map(async (s) => ({
          image: new Uint8Array(await s.blob.arrayBuffer()),
          pageUrl: s.pageUrl,
        })),
      )
    }

    mutation.mutate(
      create(CreateSupportThreadRequestSchema, {
        organizationId: orgId,
        body: trimmed,
        screenshotCaptures,
        clientContext: createSupportClientContext(),
      }),
    )
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-center gap-2 px-4 pb-3 border-b">
        <Button variant="ghost" size="icon" onClick={onBack} className="h-7 w-7" aria-label="Back to threads">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <p className="text-sm font-medium">New Thread</p>
      </div>

      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4">
        <Textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Describe your issue..."
          aria-label="Describe your issue"
          className="min-h-[120px] resize-none text-sm"
          autoFocus
        />

        <p className="text-xs text-muted-foreground">
          The page you&apos;re on is included so support can see where the issue happened.
        </p>

        <div className="flex items-center gap-2">
          <Checkbox
            id="include-screenshots"
            checked={includeScreenshots}
            onCheckedChange={(checked) => setIncludeScreenshots(checked === true)}
          />
          <label htmlFor="include-screenshots" className="text-xs text-muted-foreground cursor-pointer">
            Include screenshots?
          </label>
        </div>

        {includeScreenshots && (
          <div className="space-y-2">
            <Button
              variant="outline"
              size="sm"
              onClick={captureScreenshot}
              disabled={screenshots.length >= MAX_SCREENSHOTS || capturing}
              className="gap-1.5 text-xs"
            >
              <Camera className="h-3.5 w-3.5" />
              {capturing ? 'Capturing...' : 'Capture Screen'}
            </Button>

            {captureError && (
              <p className="text-xs text-destructive">Screenshot capture failed. Please try again.</p>
            )}

            {screenshots.length > 0 && (
              <div className="flex gap-2 pt-1">
                {screenshots.map((screenshot, index) => (
                  <div key={screenshot.url} className="relative group">
                    <img
                      src={screenshot.url}
                      alt={
                        screenshot.pageUrl
                          ? `Screenshot ${index + 1} of ${screenshot.pageUrl}`
                          : `Screenshot ${index + 1}`
                      }
                      title={screenshot.pageUrl}
                      className="h-16 w-24 rounded border object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => removeScreenshot(index)}
                      aria-label={`Remove screenshot ${index + 1}`}
                      className="absolute -top-2 -right-2 flex h-5 w-5 items-center justify-center rounded-full bg-destructive text-white"
                    >
                      <X className="h-3 w-3" strokeWidth={3} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {mutation.isError && (
          <p className="text-xs text-destructive">
            Failed to create thread. Please try again.
          </p>
        )}
      </div>

      <div className="border-t p-3">
        <Button
          onClick={handleSubmit}
          disabled={!body.trim() || mutation.isPending}
          className="w-full gap-2"
          size="sm"
        >
          <Send className="h-3.5 w-3.5" />
          {mutation.isPending ? 'Sending...' : 'Send'}
        </Button>
      </div>
    </div>
  )
}
